import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CalendarDays, MapPin, Clock, Ticket, Navigation, CalendarPlus, Download, Check, Mail, Phone, ChevronLeft, Share2 } from "lucide-react";
import { toast } from "sonner";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Reveal, Kicker, WfButton, Tag, CreditChip } from "@/components/site/primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EVENTS } from "@/content/getInvolved";
import { scrollToId } from "@/lib/scrollTo";
import { cn } from "@/lib/utils";

/**
 * EventDetailPage — TEMPLATE for all event pages. Add an entry to EVENTS in
 * content/getInvolved.js and it is automatically available at /events/:slug.
 * Registration is MOCKED until a ticketing platform is chosen.
 */

const toIcs = (ev) => {
  const d = ev.date.replace(/-/g, "");
  const s = (ev.startTime || "09:00").replace(":", "") + "00";
  const e = (ev.endTime || "17:00").replace(":", "") + "00";
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//World Farmers//Events//EN", "BEGIN:VEVENT",
    `UID:${ev.slug}@worldfarmers.org`, `DTSTART:${d}T${s}`, `DTEND:${d}T${e}`,
    `SUMMARY:${ev.title}`, `DESCRIPTION:${ev.summary}`, `LOCATION:${ev.location}, ${ev.address}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
};

const googleCalUrl = (ev) => {
  const d = ev.date.replace(/-/g, "");
  const s = (ev.startTime || "09:00").replace(":", "") + "00";
  const e = (ev.endTime || "17:00").replace(":", "") + "00";
  const p = new URLSearchParams({ action: "TEMPLATE", text: ev.title, dates: `${d}T${s}/${d}T${e}`, details: ev.summary, location: `${ev.location}, ${ev.address}` });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
};

const isPast = (ev) => new Date(`${ev.date}T23:59:59`) < new Date();

/* ---------------- Registration (MOCKED) ---------------- */
const RegistrationCard = ({ ev, past }) => {
  const [form, setForm] = useState({ name: "", email: "", guests: "1" });
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = "Please enter a valid email address.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setDone(true);
    toast.success("You’re registered! (Demo)", { description: "Ticketing isn’t connected yet — no confirmation email was sent." });
  };
  const field = "min-h-11 rounded-lg border-[var(--wf-border)] bg-white focus-visible:ring-[var(--wf-maroon)]";

  return (
    <div id="register" className="scroll-mt-24 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-6 shadow-[var(--wf-shadow)] sm:p-7" data-testid="event-registration-card">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]"><Ticket className="h-5 w-5" aria-hidden="true" /></span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--wf-green-text)]">{past ? "This event has passed" : "Registration"}</p>
          <p className="font-serif text-xl font-semibold text-[var(--wf-ink)]">{ev.cost}</p>
        </div>
      </div>
      {past ? (
        <div className="mt-5" data-testid="event-past-notice">
          <p className="text-sm leading-6 text-[var(--wf-soil)]">Thanks to everyone who joined us! Join our email list to hear about the next one first.</p>
          <WfButton as={Link} to="/contact#email-list" withArrow className="mt-4 w-full " data-testid="event-past-subscribe">Join the email list</WfButton>
        </div>
      ) : done ? (
        <div role="status" data-testid="event-register-success" className="mt-5 rounded-sm bg-[var(--wf-cream)] p-4">
          <p className="flex items-center gap-2 font-semibold text-[var(--wf-ink)]"><Check className="h-4 w-4 text-[var(--wf-green-text)]" aria-hidden="true" />See you on {ev.dateParts.month} {ev.dateParts.day}, {form.name.split(" ")[0]}!</p>
          <p className="mt-1 text-sm leading-6 text-[var(--wf-soil)]">We’ve reserved {form.guests} {form.guests === "1" ? "spot" : "spots"} under {form.email}.</p>
          <p className="mt-2 text-xs text-muted-foreground">Demo registration — ticketing platform coming soon.</p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="mt-5 space-y-4" data-testid="event-register-form">
          <div className="space-y-1.5">
            <Label htmlFor="ev-name" className="text-sm font-semibold text-[var(--wf-ink)]">Full name</Label>
            <Input id="ev-name" autoComplete="name" value={form.name} onChange={set("name")} aria-invalid={!!errors.name} data-testid="event-register-name" className={field} />
            {errors.name && <p role="alert" data-testid="event-register-name-error" className="text-sm text-[var(--wf-maroon)]">{errors.name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ev-email" className="text-sm font-semibold text-[var(--wf-ink)]">Email</Label>
            <Input id="ev-email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} data-testid="event-register-email" className={field} />
            {errors.email && <p role="alert" data-testid="event-register-email-error" className="text-sm text-[var(--wf-maroon)]">{errors.email}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ev-guests" className="text-sm font-semibold text-[var(--wf-ink)]">Number of guests</Label>
            <Select value={form.guests} onValueChange={(v) => setForm((f) => ({ ...f, guests: v }))}>
              <SelectTrigger id="ev-guests" data-testid="event-register-guests" className={cn(field, "w-full")}><SelectValue /></SelectTrigger>
              <SelectContent>{["1", "2", "3", "4", "5", "6+"].map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <WfButton type="submit" withArrow className="w-full " data-testid="event-register-submit">Reserve my spot</WfButton>
          {ev.registration?.note && <p className="text-xs leading-5 text-muted-foreground">{ev.registration.note}</p>}
        </form>
      )}
    </div>
  );
};

export default function EventDetailPage() {
  const { slug } = useParams();
  const ev = useMemo(() => EVENTS.find((e) => e.slug === slug), [slug]);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!ev) return <Navigate to="/events" replace />;
  const past = isPast(ev);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.address)}`;
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(ev.address)}&output=embed`;
  const others = EVENTS.filter((e) => e.slug !== ev.slug).slice(0, 3);

  const downloadIcs = () => {
    const blob = new Blob([toIcs(ev)], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${ev.slug}.ics`; a.click();
    URL.revokeObjectURL(url);
  };
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: ev.title, text: ev.summary, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch (_) { /* user cancelled */ }
  };

  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        {/* Hero */}
        <section id="top" className="relative flex min-h-[70vh] items-end overflow-hidden bg-[var(--wf-soil)]">
          <img src={ev.image} alt={ev.imageAlt} loading="eager" className="absolute inset-0 h-full w-full object-cover" />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/30" />
          <div className="wf-container relative z-10 pb-14 pt-36 sm:pb-20">
            <Reveal className="max-w-3xl">
              <Link to="/events" data-testid="event-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/85 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm"><ChevronLeft className="h-4 w-4" aria-hidden="true" /> All events</Link>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                {past ? <Tag>Past event</Tag> : <Tag tone="green">{ev.category}</Tag>}
                {ev.subtitle && <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/85">{ev.subtitle}</span>}
              </div>
              <h1 data-testid="event-hero-headline" className="mt-4 font-serif text-4xl font-semibold leading-[1.05] text-white [text-shadow:0_2px_24px_rgba(0,0,0,0.45)] sm:text-5xl lg:text-6xl">{ev.title}</h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-white/90 sm:text-lg">{ev.summary}</p>
              <dl className="mt-7 flex flex-wrap gap-x-8 gap-y-3 text-white">
                <div className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[var(--wf-wheat)]" aria-hidden="true" /><dt className="sr-only">Date</dt><dd className="text-sm font-semibold sm:text-base" data-testid="event-date">{ev.dateLabel}</dd></div>
                <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-[var(--wf-wheat)]" aria-hidden="true" /><dt className="sr-only">Time</dt><dd className="text-sm font-semibold sm:text-base">{ev.time}</dd></div>
                <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-[var(--wf-wheat)]" aria-hidden="true" /><dt className="sr-only">Location</dt><dd className="text-sm font-semibold sm:text-base">{ev.location} · Lancaster, MA</dd></div>
              </dl>
              {!past && (
                <div className="mt-8 flex flex-wrap gap-3">
                  <WfButton type="button" onClick={() => scrollToId("register")} variant="light" withArrow className="rounded-full px-7" data-testid="event-hero-register">Register today</WfButton>
                  <WfButton as="a" href={googleCalUrl(ev)} target="_blank" rel="noopener noreferrer" className="border border-white/60 bg-white/10 px-6 text-white backdrop-blur-sm hover:bg-white/20" data-testid="event-google-cal"><CalendarPlus className="h-4 w-4" aria-hidden="true" /> Add to Google Calendar</WfButton>
                </div>
              )}
            </Reveal>
          </div>
          <CreditChip src={ev.image} className="absolute bottom-3 right-3 z-10 rounded-full bg-black/45 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm hover:bg-black/60" />
        </section>

        {/* Body */}
        <section aria-labelledby="event-about-heading" className="wf-section bg-[var(--wf-cream)]">
          <div className="wf-container">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-7">
                <Reveal>
                  <Kicker>About this event</Kicker>
                  <h2 id="event-about-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">{ev.subtitle || ev.title}</h2>
                  <div className="mt-5 space-y-4">
                    {ev.description.map((p, i) => (<p key={i} className="max-w-[66ch] text-base leading-7 text-[var(--wf-soil)]">{p}</p>))}
                  </div>
                  {ev.highlights?.length > 0 && (
                    <ul className="mt-6 flex flex-wrap gap-2" aria-label="Highlights">
                      {ev.highlights.map((h) => (<li key={h} className="rounded-full bg-[var(--wf-wheat)] px-3 py-1.5 text-xs font-semibold text-[var(--wf-maroon)]">{h}</li>))}
                    </ul>
                  )}
                </Reveal>

                {ev.schedule?.length > 0 && (
                  <Reveal delay={0.1} className="mt-12">
                    <h3 className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">Schedule</h3>
                    <ol className="mt-5 border-l-2 border-[var(--wf-border)]" data-testid="event-schedule">
                      {ev.schedule.map((s) => (
                        <li key={s.time} className="relative pb-6 pl-6 last:pb-0">
                          <span aria-hidden="true" className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-[var(--wf-maroon)] ring-4 ring-[var(--wf-cream)]" />
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--wf-green-text)]">{s.time}</p>
                          <p className="mt-0.5 font-semibold text-[var(--wf-ink)]">{s.title}</p>
                          <p className="mt-0.5 text-sm leading-6 text-[var(--wf-soil)]">{s.body}</p>
                        </li>
                      ))}
                    </ol>
                  </Reveal>
                )}

                {ev.gallery?.length > 0 && (
                  <Reveal delay={0.1} className="mt-12">
                    <h3 className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">From past tours</h3>
                    <ul className="mt-5 grid grid-cols-3 gap-3">
                      {ev.gallery.map((src) => (<li key={src}><img src={src} alt="Farmers and visitors in the fields at the Flats Mentor Farm" loading="lazy" className="aspect-square w-full rounded-sm object-cover" /></li>))}
                    </ul>
                  </Reveal>
                )}
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-5">
                <div className="space-y-5 lg:sticky lg:top-24">
                  <Reveal><RegistrationCard ev={ev} past={past} /></Reveal>
                  <Reveal delay={0.05}>
                    <div className="overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)]">
                      <iframe title={`Map to ${ev.location}`} src={mapEmbed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-48 w-full border-0" data-testid="event-map" />
                      <div className="p-5">
                        <p className="font-semibold text-[var(--wf-ink)]">{ev.location}</p>
                        <p className="text-sm text-muted-foreground">{ev.address}</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="event-directions" className="inline-flex min-h-11 items-center gap-2 rounded-sm bg-[var(--wf-farm-green)] px-4 py-2 text-sm font-semibold text-white hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Navigation className="h-4 w-4" aria-hidden="true" />Directions</a>
                          <button type="button" onClick={downloadIcs} data-testid="event-ics" className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-[var(--wf-border)] px-4 py-2 text-sm font-semibold text-[var(--wf-ink)] hover:border-[var(--wf-maroon)] hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Download className="h-4 w-4" aria-hidden="true" />.ics</button>
                          <button type="button" onClick={share} data-testid="event-share" className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-[var(--wf-border)] px-4 py-2 text-sm font-semibold text-[var(--wf-ink)] hover:border-[var(--wf-maroon)] hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Share2 className="h-4 w-4" aria-hidden="true" />Share</button>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <div className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-5">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--wf-green-text)]">Questions?</p>
                      <p className="mt-1 font-semibold text-[var(--wf-ink)]">{ev.contact.name}</p>
                      <ul className="mt-2 space-y-1.5 text-sm">
                        <li><a href={`mailto:${ev.contact.email}`} className="inline-flex items-center gap-2 text-[var(--wf-maroon)] underline-offset-4 hover:underline"><Mail className="h-4 w-4" aria-hidden="true" />{ev.contact.email}</a></li>
                        <li><a href={`tel:${ev.contact.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-2 text-[var(--wf-maroon)] underline-offset-4 hover:underline"><Phone className="h-4 w-4" aria-hidden="true" />{ev.contact.phone}</a></li>
                      </ul>
                    </div>
                  </Reveal>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {others.length > 0 && (
          <section aria-labelledby="event-more-heading" className="wf-section bg-white">
            <div className="wf-container">
              <Kicker>More events</Kicker>
              <h2 id="event-more-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)]">You might also like</h2>
              <ul className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                {others.map((o) => (<li key={o.slug}><Link to={`/events/${o.slug}`} className="block rounded-sm border border-[var(--wf-border)] p-5 hover:border-[var(--wf-maroon)]"><p className="text-xs text-muted-foreground">{o.dateLabel}</p><p className="mt-1 font-serif text-xl font-semibold text-[var(--wf-ink)]">{o.title}</p></Link></li>))}
              </ul>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </>
  );
}
